// v2/components/CategoryAnalytics.js
// Kategori bazli alim dagilimi — /api/v2/dashboard/ categories
// Kaynak: B2B siparisleri, TUTAR bazli (adet yaniltici: ampul gibi ucuz urunler sisiriyor)
import React, { useState, useCallback } from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { RotateCw } from 'lucide-react';
import { CHART_COLORS } from '../constants/theme';
import { formatNumber, formatPercent, formatCompact, formatTRY } from '../utils/format';
import layout from '../styles/HomeLayout.module.css';
import styles from './CategoryAnalytics.module.css';

const DIGER_RENK = '#CBD5E1';
const ANA_RENKLER = CHART_COLORS.slice(0, 5);

const renk = (it, i) => (it.other ? DIGER_RENK : ANA_RENKLER[i % ANA_RENKLER.length]);
const kisaTL = (v) => `${formatCompact(v)} ₺`;

function BarTip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipLabel}>{label}</div>
      <div>{formatTRY(payload[0].value)}</div>
    </div>
  );
}

function Kabuk({ yil, children }) {
  return (
    <section className={layout.panel} aria-label="Kategori Bazlı Alım Dağılımı">
      <div className={layout.panelHead}>
        <span className={layout.bar} />
        <h2 className={layout.panelTitle}>Kategori Bazlı Alım Dağılımı</h2>
        <span className={layout.headSpacer} />
        <span className={layout.headChip} title="B2B üzerinden verdiğiniz siparişlere göre">
          {yil ? `${yil} · B2B` : 'B2B'}
        </span>
      </div>
      {children}
    </section>
  );
}

export default function CategoryAnalytics({ data, loading = false, error = null, onRetry }) {
  const [active, setActive] = useState(null);
  const enter = useCallback((i) => setActive(i), []);
  const leave = useCallback(() => setActive(null), []);
  const toggle = useCallback((i) => setActive((cur) => (cur === i ? null : i)), []);

  if (!data) {
    return (
      <Kabuk>
        <div className={styles.state}>
          {loading ? 'Yükleniyor…' : (
            <>
              <span>{error || 'Kategori verisi alınamadı.'}</span>
              {onRetry && (
                <button type="button" className={styles.retry} onClick={onRetry}>
                  <RotateCw size={12} /> Tekrar dene
                </button>
              )}
            </>
          )}
        </div>
      </Kabuk>
    );
  }

  const items = data.items || [];
  if (!items.length) {
    return (
      <Kabuk yil={data.year}>
        <div className={styles.state}>Bu yıl henüz B2B siparişiniz bulunmuyor.</div>
      </Kabuk>
    );
  }

  const secili = active !== null && items[active] ? active : null;
  const focus = secili !== null ? items[secili] : null;
  const detayIdx = secili !== null ? secili : 0;
  const detay = items[detayIdx];

  return (
    <Kabuk yil={data.year}>
      <div className={styles.body}>
        <div className={styles.donutWrap} onMouseLeave={leave}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={items} dataKey="amount" nameKey="name"
                innerRadius="64%" outerRadius="100%"
                paddingAngle={items.length > 1 ? 2 : 0} stroke="none"
                isAnimationActive animationDuration={520}
              >
                {items.map((it, i) => (
                  <Cell
                    key={it.name}
                    fill={renk(it, i)}
                    opacity={secili === null || secili === i ? 1 : 0.3}
                    style={{ cursor: 'pointer', transition: 'opacity .15s' }}
                    onMouseEnter={() => enter(i)}
                    onClick={() => toggle(i)}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className={styles.donutCenter}>
            <span className={styles.donutLabel}>{focus ? focus.name : 'Toplam Alım'}</span>
            <span className={styles.donutValue}>
              {focus ? formatPercent(focus.pct, 1) : kisaTL(data.totalAmount)}
            </span>
          </div>
        </div>

        <div className={styles.legend} onMouseLeave={leave}>
          {items.map((it, i) => (
            <div
              key={it.name}
              className={`${styles.legRow} ${secili === i ? styles.legRowOn : ''}`}
              onMouseEnter={() => enter(i)}
              onClick={() => toggle(i)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(i); }
              }}
              role="button" tabIndex={0}
              title={`${formatTRY(it.amount)} · ${formatNumber(it.qty)} adet`}
            >
              <span className={styles.dot} style={{ background: renk(it, i) }} />
              <span className={styles.legName}>{it.name}</span>
              <span className={styles.legPct}>{formatPercent(it.pct, 1)}</span>
              <span className={styles.legCount}>{kisaTL(it.amount)}</span>
            </div>
          ))}
        </div>

        <div className={styles.chartBox}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.monthly} margin={{ top: 6, right: 4, left: -22, bottom: 0 }}>
              <defs>
                <linearGradient id="catBar" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" />
                  <stop offset="100%" stopColor="#93C5FD" />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="m" tickLine={false} axisLine={false}
                     tick={{ fontSize: 10, fill: '#94A3B8' }} />
              <Tooltip content={<BarTip />} cursor={{ fill: 'rgba(47,128,237,.06)' }} />
              <Bar dataKey="v" fill="url(#catBar)" radius={[4, 4, 0, 0]} maxBarSize={26}
                   isAnimationActive animationDuration={620} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className={styles.topList}>
          <h3 className={styles.topTitle}>
            <span className={styles.dot} style={{ background: renk(detay, detayIdx) }} />
            {detay.other ? 'Diğer kategoriler' : `${detay.name} içinde`}
          </h3>
          {detay.subcategories.map((s) => (
            <div key={s.name} className={styles.topRow} title={formatTRY(s.amount)}>
              <span className={styles.topName}>{s.name}</span>
              <span className={styles.topCount}>{formatPercent(s.pct, 0)}</span>
            </div>
          ))}
        </div>
      </div>
    </Kabuk>
  );
}
